import { LayoutGrid, Table2 } from 'lucide-react';
import { PIPELINE_STAGES, STAGE_LABELS, PipelineStage } from '../../types/pipeline';

export interface PipelineFilterState {
  search: string;
  client: string; // client_entity_name or '' for all
  stage: PipelineStage | '';
  needsHumanOnly: boolean;
}

export type PipelineViewMode = 'board' | 'table';

interface Props {
  filters: PipelineFilterState;
  onChange: (next: PipelineFilterState) => void;
  view: PipelineViewMode;
  onViewChange: (v: PipelineViewMode) => void;
  clients: string[]; // distinct client_entity_name values present on the board
}

export function PipelineFilters({ filters, onChange, view, onViewChange, clients }: Props) {
  const set = (patch: Partial<PipelineFilterState>) => onChange({ ...filters, ...patch });
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={filters.search}
        onChange={(e) => set({ search: e.target.value })}
        placeholder="Search projects…"
        aria-label="Search projects"
        className="h-9 px-3 text-sm rounded-md border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10"
      />
      <select
        value={filters.client}
        onChange={(e) => set({ client: e.target.value })}
        aria-label="Filter by client"
        className="h-9 px-2 text-sm rounded-md border border-neutral-200 bg-white"
      >
        <option value="">All clients</option>
        {clients.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <select
        value={filters.stage}
        onChange={(e) => set({ stage: e.target.value as PipelineStage | '' })}
        aria-label="Filter by stage"
        className="h-9 px-2 text-sm rounded-md border border-neutral-200 bg-white"
      >
        <option value="">All stages</option>
        {PIPELINE_STAGES.map((s) => (
          <option key={s} value={s}>{STAGE_LABELS[s]}</option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 text-sm text-neutral-600">
        <input
          type="checkbox"
          checked={filters.needsHumanOnly}
          onChange={(e) => set({ needsHumanOnly: e.target.checked })}
          className="rounded border-neutral-300"
        />
        Needs you
      </label>

      <div className="ml-auto flex items-center rounded-md border border-neutral-200 overflow-hidden">
        <button
          onClick={() => onViewChange('board')}
          aria-label="Board view"
          className={`h-9 px-2.5 flex items-center ${view === 'board' ? 'bg-primary-600 text-white' : 'text-neutral-500 hover:bg-neutral-50'}`}
        >
          <LayoutGrid className="w-4 h-4" />
        </button>
        <button
          onClick={() => onViewChange('table')}
          aria-label="Table view"
          className={`h-9 px-2.5 flex items-center ${view === 'table' ? 'bg-primary-600 text-white' : 'text-neutral-500 hover:bg-neutral-50'}`}
        >
          <Table2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
